// websocket.js
import { useEffect, useRef, useCallback } from 'react';

const FIRST_RETRY_MIN_MS = 250;
const FIRST_RETRY_MAX_MS = 1500;
const MAX_RETRY_MS = 5000;
// A connection that closes sooner than this after opening (the server rejected
// it) counts as a failed attempt, so rejections keep backing off.
const STABLE_CONNECTION_MS = 5000;

/**
 * Delay before the next reconnect: a random 250-1500 ms after a connection
 * drops, so the tabs a restarting backend pod disconnects do not all reconnect
 * at once, with the range doubling for each failed attempt up to 5 s.
 */
export const reconnectDelayMs = (failedAttempts, random = Math.random) => {
    const max = Math.min(MAX_RETRY_MS, FIRST_RETRY_MAX_MS * 2 ** failedAttempts);
    const min = Math.min(FIRST_RETRY_MIN_MS * 2 ** failedAttempts, max);
    return min + random() * (max - min);
};

export const useWebSocket = (wsUrl, onMessageReceived, onReconnect) => {
    const ws = useRef(null);
    const reconnectTimeout = useRef(null);
    const isConnecting = useRef(false);
    // True after a connection closed while the hook is still mounted, so the
    // next successful open is a reconnect. Kept in a ref (like onReconnect)
    // so it never re-triggers the connect effect.
    const hadDisconnect = useRef(false);
    const failedAttempts = useRef(0);
    const openedAt = useRef(null);
    const onReconnectRef = useRef(onReconnect);
    onReconnectRef.current = onReconnect;

    const connectWebSocket = useCallback(() => {
        if (isConnecting.current || (ws.current?.readyState === WebSocket.OPEN)) {
            return;
        }

        const scheduleReconnect = () => {
            if (openedAt.current !== null && Date.now() - openedAt.current >= STABLE_CONNECTION_MS) {
                failedAttempts.current = 0;
            }
            openedAt.current = null;
            const delay = reconnectDelayMs(failedAttempts.current);
            failedAttempts.current += 1;
            reconnectTimeout.current = setTimeout(() => {
                reconnectTimeout.current = null;
                connectWebSocket();
            }, delay);
        };

        try {
            isConnecting.current = true;

            if (ws.current) {
                ws.current.close();
            }

            // Create WebSocket only once
            ws.current = new WebSocket(wsUrl);
            console.log('Attempting to connect to:', wsUrl);

            ws.current.onopen = () => {
                console.log('WebSocket Connected');
                isConnecting.current = false;
                openedAt.current = Date.now();
                if (hadDisconnect.current) {
                    hadDisconnect.current = false;
                    // Messages published while disconnected are lost for good
                    // (the server relay has no replay), so let the consumer
                    // resync its realtime-fed state from the server.
                    onReconnectRef.current?.();
                }
            };

            ws.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    onMessageReceived(data, wsUrl);
                } catch (error) {
                    console.error('Error processing message:', error);
                }
            };

            ws.current.onerror = (error) => {
                console.error('WebSocket Error:', error);
                isConnecting.current = false;
            };

            ws.current.onclose = (event) => {
                console.log('WebSocket Closed. Code:', event.code, 'Reason:', event.reason);
                isConnecting.current = false;

                // Only reconnect if we're not unmounting
                if (ws.current) {
                    hadDisconnect.current = true;
                    scheduleReconnect();
                }
            };

        } catch (error) {
            console.error('Error in connectWebSocket:', error);
            isConnecting.current = false;
            if (ws.current) {
                scheduleReconnect();
            }
        }
    }, [wsUrl]);

    useEffect(() => {
        console.log('useWebSocket effect running');
        connectWebSocket();

        return () => {
            console.log('Cleaning up WebSocket connection');
            if (reconnectTimeout.current) {
                clearTimeout(reconnectTimeout.current);
                reconnectTimeout.current = null;
            }
            if (ws.current) {
                ws.current.onopen = null;
                ws.current.onmessage = null;
                ws.current.onerror = null;
                ws.current.onclose = null;
                
                ws.current.close();
                ws.current = null;
                isConnecting.current = false;
            }
        };
    }, [connectWebSocket]);

    // Hidden tabs run timers late (Chrome can hold them for a minute), so a tab
    // that comes back while a reconnect is pending reconnects at once.
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible' || !reconnectTimeout.current) {
                return;
            }
            clearTimeout(reconnectTimeout.current);
            reconnectTimeout.current = null;
            connectWebSocket();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [connectWebSocket]);

    return ws.current;
};
